import type { TOtaMealPlan } from "../HotelOtaMealPlans"
import type { THotelOtaRoomType } from "../HotelOtaRoomTypes"
import {
	Badge,
	Box,
	Icons,
	Inline,
	Stack,
	Text,
	Tooltip,
} from "@sembark-travel/ui-base"
import type { TBaseOccupancyChange } from "./utils"
import {
	COLUMN_PILL_COLORS,
	GRID_BORDER_COLOR,
	ROOM_OCCUPANCY_COL_WIDTH,
	type TColumnPillVariant,
} from "./gridStyles"

export function ColumnHeaderPill({
	label,
	variant,
	paddingX = "2",
	diffStatus,
}: {
	label: string
	variant: TColumnPillVariant
	paddingX?: "1" | "2" | "3"
	// Set when the column itself was gained or lost between two versions of a
	// sheet, rather than any rate under it having moved
	diffStatus?: "added" | "removed" | null
}) {
	const isRemoved = diffStatus === "removed"
	return (
		<Box
			display="inlineBlock"
			paddingX={paddingX}
			paddingY="1"
			rounded="md"
			style={{
				...COLUMN_PILL_COLORS[variant],
				// A lost column keeps its own colouring, dimmed — the strike-through
				// on the label is what says it went
				opacity: isRemoved ? 0.7 : undefined,
			}}
		>
			<Text
				fontWeight="semibold"
				color={diffStatus === "added" ? "primary" : undefined}
				textDecoration={isRemoved ? "lineThrough" : undefined}
				whiteSpace="nowrap"
			>
				{label}
			</Text>
		</Box>
	)
}

// Only the BASE cell of a room's header row labels the occupancy it is priced
// for. Cells are middle aligned, so that extra line would push the BASE input
// below every other input in the row — the sibling cells render the same slot
// empty to keep the row's inputs on one line.
export function OccupancyBadgeSlot({ label }: { label?: string }) {
	return (
		<Inline
			justifyContent="center"
			aria-hidden={label ? undefined : true}
			style={label ? undefined : { visibility: "hidden" }}
		>
			<Box
				display="inlineBlock"
				paddingX="1"
				rounded="sm"
				style={COLUMN_PILL_COLORS.lower}
			>
				<Text fontSize="sm" fontWeight="semibold">
					{label || "-"}
				</Text>
			</Box>
		</Inline>
	)
}

export function MiniCurrency({ currency }: { currency: string }) {
	return (
		<Text style={{ fontSize: "9px" }} color="muted" fontFamily="mono">
			{currency}
		</Text>
	)
}

function MealPlanBadge({
	mealPlan,
	isBase,
	style,
}: {
	mealPlan: TOtaMealPlan
	isBase?: boolean
	style?: React.CSSProperties
}) {
	if (isBase) {
		return (
			<Badge success style={style}>
				★ {mealPlan.meal_category || mealPlan.name.slice(0, 3)}
			</Badge>
		)
	}
	if (!mealPlan.meal_category) return null
	return (
		<Badge style={{ background: "transparent", ...style }}>
			{mealPlan.meal_category}
		</Badge>
	)
}

// The room column is sticky, so every cell in it has to paint its own
// background — a transparent one would let the scrolled rate cells show
// through.
function stickyRoomCellStyle(backgroundColor: string) {
	return {
		borderColor: GRID_BORDER_COLOR,
		width: `calc(${ROOM_OCCUPANCY_COL_WIDTH} - 50px)`,
		maxWidth: ROOM_OCCUPANCY_COL_WIDTH,
		position: "sticky" as const,
		left: 0,
		zIndex: 1,
		backgroundColor,
	}
}

// The sticky first cell of a room's header row: the room type, what it can
// take, and the meal plan its header row is priced for. `actions` is where the
// editor hangs its remove-room control; the preview passes nothing.
export function RoomInfoCell({
	roomType,
	baseOccupancy,
	baseOccupancyChange,
	baseMealPlan,
	actions,
	diffStatus,
}: {
	roomType: THotelOtaRoomType
	baseOccupancy: number
	// Set when the hotel changed the room's base occupancy after the sheet was
	// saved — the editor clears its BASE price, this says why
	baseOccupancyChange?: TBaseOccupancyChange
	baseMealPlan: TOtaMealPlan | null
	actions?: React.ReactNode
	diffStatus?: "added" | "removed" | null
}) {
	const isAdded = diffStatus === "added"
	const isRemoved = diffStatus === "removed"
	const bgColor = isAdded ? "#EDF2FE" : isRemoved ? "#E0E1E6" : "#FFFFFF"

	return (
		<Box
			as="td"
			padding="2"
			verticalAlign="middle"
			borderRightWidth="1"
			borderBottomWidth="1"
			style={stickyRoomCellStyle(bgColor)}
		>
			<Stack gap="2">
				<Inline justifyContent="between" alignItems="center">
					<Stack gap="1" minWidth="0">
						<Text
							fontWeight="semibold"
							color={isAdded ? "primary" : isRemoved ? "muted" : undefined}
							textDecoration={isRemoved ? "lineThrough" : undefined}
							textOverflow="truncate"
							title={roomType.name}
						>
							{roomType.name}
						</Text>
						{roomType.max_occupancy ? (
							<Text
								fontSize="xs"
								fontWeight="semibold"
								color="muted"
								textDecoration={isRemoved ? "lineThrough" : undefined}
								whiteSpace="nowrap"
							>
								Max {roomType.max_adult_occupancy || 0}A
								{roomType.max_child_occupancy
									? `| ${roomType.max_child_occupancy} C`
									: null}
							</Text>
						) : null}
					</Stack>
					<Inline alignItems="center">
						{actions}
						<Inline
							gap="2"
							paddingX="2"
							paddingY="1"
							rounded="md"
							style={{
								backgroundColor: "#DFF2FE",
								color: "#00749E",
								opacity: isRemoved ? 0.7 : 1,
							}}
							flexWrap="nowrap"
							alignItems="center"
						>
							<Text
								fontSize="sm"
								fontWeight="semibold"
								whiteSpace="nowrap"
								textDecoration={isRemoved ? "lineThrough" : undefined}
							>
								<Icons.Users2 size="3" /> {baseOccupancy}
							</Text>
							{baseOccupancyChange ? (
								<Tooltip
									content={
										<Text fontSize="sm">
											Updated from {baseOccupancyChange.from} →{" "}
											{baseOccupancyChange.to}
										</Text>
									}
								>
									<Box paddingRight="1">
										<Icons.Attention color="warning_light" size="4" />
									</Box>
								</Tooltip>
							) : null}
							{roomType.base_child_occupancy ? (
								<Text
									fontSize="sm"
									fontWeight="semibold"
									whiteSpace="nowrap"
									textDecoration={isRemoved ? "lineThrough" : undefined}
								>
									<Icons.Child size="3" /> {roomType.base_child_occupancy}
								</Text>
							) : null}
						</Inline>
					</Inline>
				</Inline>

				{baseMealPlan ? (
					<MealPlanRowLabel
						mealPlan={baseMealPlan}
						isBase
						color={isAdded ? "primary" : isRemoved ? "muted" : undefined}
						textDecoration={isRemoved ? "lineThrough" : undefined}
						badgeStyle={isRemoved ? { opacity: 0.7 } : undefined}
					/>
				) : null}
			</Stack>
		</Box>
	)
}

// The sticky first cell of a meal plan sub-row.
export function MealPlanRowCell({
	mealPlan,
	diffStatus,
}: {
	mealPlan: TOtaMealPlan
	diffStatus?: "added" | "removed" | null
}) {
	const isAdded = diffStatus === "added"
	const isRemoved = diffStatus === "removed"
	const bgColor = isAdded ? "#EDF2FE" : isRemoved ? "#E0E1E6" : "#F3FEFA"
	const textColor = isAdded ? "primary" : isRemoved ? "muted" : "#006045"

	return (
		<Box
			as="td"
			padding="2"
			verticalAlign="middle"
			borderRightWidth="1"
			borderBottomWidth="1"
			style={{
				...stickyRoomCellStyle(bgColor),
				color: textColor,
				fontWeight: 500,
			}}
		>
			<MealPlanRowLabel
				mealPlan={mealPlan}
				color={isAdded ? "primary" : isRemoved ? "muted" : "success"}
				textDecoration={isRemoved ? "lineThrough" : undefined}
				badgeStyle={isRemoved ? { opacity: 0.7 } : undefined}
			/>
		</Box>
	)
}

function MealPlanRowLabel({
	mealPlan,
	isBase,
	color,
	textDecoration,
	badgeStyle,
}: {
	mealPlan: TOtaMealPlan
	isBase?: boolean
	color?: "success" | "primary" | "muted"
	textDecoration?: "lineThrough" | "underline"
	badgeStyle?: React.CSSProperties
}) {
	return (
		<Inline
			justifyContent="between"
			alignItems="center"
			gap="2"
			paddingLeft="3"
		>
			<Text
				fontWeight="semibold"
				color={color || "success"}
				textDecoration={textDecoration}
				textOverflow="truncate"
				title={mealPlan.name}
			>
				{mealPlan.name}
			</Text>
			<MealPlanBadge mealPlan={mealPlan} isBase={isBase} style={badgeStyle} />
		</Inline>
	)
}
